import crypto from "crypto";
import { AxiosResponse } from "axios";
import Cookies from "js-cookie";
import { BO_CORE_DEFAULT_VALUE, getAxiosInstances, getLocaleNameByTag, isCancelledError, toastError } from "../AppUtils";
import store from "../../store";
import { actions } from "../../reducers/userReducer";
import { PublicRoutes, UserRoles } from "../appEnums/AppEnums";
import { LOCAL_STORAGE_ITEM_LOCALE } from "../../config/app.config";
import AxiosActions from "../../config/axios/AxiosActions";
import { durationToExactDate, getLocales } from "../DateTimeUtils";

export interface UserPreference {
    locale: { value: string; label: string };
}

export const AVAILABLE_USER_ROLES_IN_ACTIVE_MANAGEMENT_MODE = [UserRoles.Employee, UserRoles.Manager, UserRoles.SA];

let authUser: any = null;

const SESSION_STORAGE_ACCESS_TOKEN = "access_token";

const SESSION_STORAGE_CUSTOMER_SERVICE_ACCESS_TOKEN = "customer_service_access_token";

const SESSION_STORAGE_REFRESH_TOKEN = "refresh_token";

const SESSION_STORAGE_CUSTOMER_SERVICE_REFRESH_TOKEN = "customer_service_refresh_token";

const SESSION_STORAGE_CODE_VERIFIER = "code_verifier";

export function getAuthUser() {
    return authUser;
}

export function setAuthUser(value) {
    authUser = value;
}

export const getAccessToken = () => {
    return Cookies.get(SESSION_STORAGE_ACCESS_TOKEN);
};

export const isRefreshTokenPresent = (): boolean => {
    return !!Cookies.get(SESSION_STORAGE_REFRESH_TOKEN);
};

export const isAccessTokenPresent = (): boolean => {
    return !!Cookies.get(SESSION_STORAGE_ACCESS_TOKEN);
};

export const getRefreshToken = () => {
    return Cookies.get(SESSION_STORAGE_REFRESH_TOKEN);
};

export const isAssumingIdentity = () => {
    return !!Cookies.get(SESSION_STORAGE_CUSTOMER_SERVICE_ACCESS_TOKEN);
};

export const mySelfAuthHelper = (showSpinner?: boolean, signal?: AbortSignal): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `user/myself`, { signal }, showSpinner === true);
};

export const mutateMySelfResponse = (response: AxiosResponse) => {
    const mutatedResponse = response;

    if (mutatedResponse.data.customerSupport) {
        mutatedResponse.data.roles.push(UserRoles.CS);
    }

    return mutatedResponse;
};

export const updateUserLocaleAuthHelper = (localeTag: string): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `user/locale?locale=${localeTag}`, {}, {}, false);
};

export const clearTokenCookies = () => {
    Cookies.remove(SESSION_STORAGE_ACCESS_TOKEN);
    Cookies.remove(SESSION_STORAGE_REFRESH_TOKEN);
};

export const clearAssumedTokenCookies = () => {
    Cookies.remove(SESSION_STORAGE_CUSTOMER_SERVICE_ACCESS_TOKEN);
    Cookies.remove(SESSION_STORAGE_CUSTOMER_SERVICE_REFRESH_TOKEN);
};

export const processMySelfRequestError = (e) => {
    if (isCancelledError(e)) {
        return;
    }

    store.dispatch({ type: "set", showSpinner: false });
    store.dispatch({ type: actions.FETCH_AUTH_USER_FAILED });
    clearTokenCookies();
    clearAssumedTokenCookies();

    window.location.href = `${PublicRoutes.INIT_AUTH}`;
};

export const setTokenCookies = (accessToken: string, refreshToken: string, accessTokenExpiry: number, _refreshTokenExpiry: number) => {
    // debug
    // console.log("set: accessToken");
    // console.log("set: refreshToken");
    // setTimeout(() => console.log("expired: accessToken"), accessTokenExpiry * 1000);
    // setTimeout(() => console.log("expired: refreshToken"), accessTokenExpiry * 2 * 1000);

    Cookies.set(SESSION_STORAGE_ACCESS_TOKEN, accessToken, { expires: durationToExactDate(accessTokenExpiry, "second") });
    Cookies.set(SESSION_STORAGE_REFRESH_TOKEN, refreshToken || "", {
        expires: durationToExactDate(accessTokenExpiry * 2, "second"), // todo when be starts returning expiry
    });
};

export const processMySelfRequest = (response: AxiosResponse) => {
    const mutatedResponse = mutateMySelfResponse(response);
    setAuthUser(mutatedResponse.data);
    store.dispatch({ type: actions.AUTH_USER, payload: getAuthUser() });
    return mutatedResponse;
};

export const logoutHelper = () => {
    return AxiosActions.post(getAxiosInstances().AUTH, "user/logout", {}, { withCredentials: true }, false);
};

export const setAssumeIdentityTokenCookies = (accessToken: string, refreshToken: string, accessTokenExpiry: number, _refreshTokenExpiry: number) => {
    const originalAccessToken = getAccessToken() as string;
    const originalRefreshToken = getRefreshToken() as string;

    Cookies.set(SESSION_STORAGE_CUSTOMER_SERVICE_ACCESS_TOKEN, originalAccessToken, {
        expires: durationToExactDate(accessTokenExpiry, "second"),
    });
    Cookies.set(SESSION_STORAGE_CUSTOMER_SERVICE_REFRESH_TOKEN, originalRefreshToken, {
        expires: durationToExactDate(accessTokenExpiry * 2, "second"),
    }); // todo when be starts returnig expiry

    setTokenCookies(accessToken, refreshToken, accessTokenExpiry, _refreshTokenExpiry);
    mySelfAuthHelper(true).then(processMySelfRequest).catch(processMySelfRequestError);
};

export const setCodeVerifierCookies = (codeVerifier: string, codeVerifierExpiry: number) => {
    Cookies.set(SESSION_STORAGE_CODE_VERIFIER, codeVerifier, { expires: durationToExactDate(codeVerifierExpiry, "second") });
};

export const retrievePreferredLocale = (myselfLocale: any) => {
    if (myselfLocale && myselfLocale !== BO_CORE_DEFAULT_VALUE) {
        return myselfLocale;
    }
    const locale: { label: string; value: string } = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_ITEM_LOCALE) as string);
    return locale ? locale.value : getLocales();
};

export const getCodeVerifierCookies = () => {
    return Cookies.get(SESSION_STORAGE_CODE_VERIFIER);
};

export const removeCodeVerifierCookies = () => {
    Cookies.remove(SESSION_STORAGE_CODE_VERIFIER);
};

export const objectToEncodedRequestBody = (object: any): string => {
    const formBody: string[] = [];

    Object.keys(object).forEach((property: string) => {
        const encodedKey = encodeURIComponent(property);
        const encodedValue = encodeURIComponent(object[property]);
        formBody.push(`${encodedKey}=${encodedValue}`);
    });

    return formBody.join("&");
};

export const updateUserLocale = (myselfLocale: any, currentUserPreference: UserPreference) => {
    const preferredLocale = retrievePreferredLocale(myselfLocale);
    if (preferredLocale && preferredLocale !== currentUserPreference.locale.value) {
        store.dispatch({
            type: actions.SET_APPLICATION_LOCALE,
            payload: {
                value: preferredLocale,
                label: getLocaleNameByTag(preferredLocale),
            },
        });
    }
    const saveUserPreferredLocale = preferredLocale !== myselfLocale;
    if (saveUserPreferredLocale) {
        updateUserLocaleAuthHelper(preferredLocale).catch(() => {
            toastError("Failed saving user preferred location.");
        });
    }
};

export const updateUserPreferences = (myselfResponse: any, currentUserPreference: UserPreference) => {
    updateUserLocale(myselfResponse.locale, currentUserPreference);
};

export const mySelfRequest = (userPreference: UserPreference, signal?: AbortSignal) => {
    return mySelfAuthHelper(true, signal)
        .then((response) => {
            updateUserPreferences(response.data, userPreference);
            return response;
        })
        .then(processMySelfRequest)
        .catch(processMySelfRequestError);
};

export const serverLogOut = () => {
    return logoutHelper().then(() => {
        clearTokenCookies();

        if (isAssumingIdentity()) {
            clearAssumedTokenCookies();
        }

        window.location.href = `${PublicRoutes.LOGIN}`;
    });
};

// https://auth0.com/docs/get-started/authentication-and-authorization-flow/call-your-api-using-the-authorization-code-flow-with-pkce
// Dependency: Node.js crypto module
// https://nodejs.org/api/crypto.html#crypto_crypto
export const base64URLEncode = (str) => {
    return str.toString("base64").replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

// Dependency: Node.js crypto module
// https://nodejs.org/api/crypto.html#crypto_crypto
export const sha256 = (buffer) => {
    return crypto.createHash("sha256").update(buffer).digest();
};
